import React from 'react';

import { useAutoNavigate } from '@betterment/js-runtime';
import { ContentLayoutF, H1, Icon, IconButton, PancakeStack, WorkflowHeader } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { ErrorBoundary } from '@betterment/shared-components';
import { useParams } from 'react-router-dom';

import { AccountProjectionCard } from './account/components/projection-card/AccountProjectionCard';
import { AccountSettingsCard } from './account/components/settings-card/AccountSettingsCard';
import { ForecasterAccountProvider } from './components/ForecasterAccountProvider';
import { ForecasterPurposeProvider } from './components/ForecasterPurposeProvider';
import { HowToSaveCard } from './purpose/components/how-to-save-card/HowToSaveCard';
import { PurposeProjectionCard } from './purpose/components/projection-card/PurposeProjectionCard';
import { RetirementSpendingPowerCard } from './purpose/components/retirement-spending-power-card/RetirementSpendingPowerCard';
import { PurposeSettingsCard } from './purpose/components/settings-card/PurposeSettingsCard';
import { ForecasterPageTitle } from './shared/components/ForecasterPageTitle';
import { PremiumOnboardingCard } from './shared/components/premium-onboarding-card/PremiumOnboardingCard';

import { WithLayout } from '../RetailApp/components/WithLayout';
import { urlBuilder } from '../Shared/urlBuilder';

// We need to reverse ContentLayoutF, but don't have a strong signal on what it should be.
const BS_REVERSE = css.raw({
  '@container (min-width: calc({sizes.layout.slotMinWidth} * 2 + {spacing.space.400}))': {
    gap: 'space.400',
    gridTemplateColumns: 'minmax(token(sizes.layout.slotMinWidth), 30%) 1fr',
    gridTemplateAreas: `'slot-1 slot-2'`,
  },
});

const PROJECTION_SLOT_CSS = css.raw({
  display: 'grid',
  gap: { base: 'space.100', sm: 'space.300' },
  gridAutoRows: 'max-content',
});

export const ForecasterPage: React.FC = () => {
  const navigate = useAutoNavigate();
  const { purposeId, accountId } = useParams<{ purposeId?: string; accountId?: string }>();

  const handleClose = () => {
    if (purposeId) {
      void navigate(urlBuilder.appPurpose.absolute({ purposeId }));
    } else if (accountId) {
      void navigate(urlBuilder.appAccount.absolute({ accountId }));
    } else {
      void navigate(urlBuilder.appHome.absolute());
    }
  };

  return (
    <ErrorBoundary fallbackType="full" feature="forecaster" owner="user_engagement">
      <WithLayout parts={{ sidebar: false, footer: true }}>
        <PancakeStack css={{ maxWidth: '100%' }}>
          <PancakeStack.Header
            css={{
              maxWidth: '100%',
              bg: 'background.surface',
              px: 'space.300',
              borderBottom: '1px solid token(colors.border.weak)',
            }}
          >
            <WorkflowHeader css={{ px: 'space.0', flex: 1, justifySelf: 'center', width: '100%' }}>
              <WorkflowHeader.Leading css={{ display: 'flex', alignItems: 'center', gap: 'space.100' }}>
                <Icon name="Guidance" color="icon.accent.gold" size="small" />
                <H1 variant="title100">Forecaster</H1>
              </WorkflowHeader.Leading>
              <WorkflowHeader.Title>
                <ForecasterPageTitle />
              </WorkflowHeader.Title>
              <WorkflowHeader.Trailing>
                <IconButton name="X" onClick={handleClose} label="Close" />
              </WorkflowHeader.Trailing>
            </WorkflowHeader>
          </PancakeStack.Header>
          <PancakeStack.Main css={{ p: 'space.300', bg: 'background', smDown: { px: 'space.0', py: 'space.100' } }}>
            <ContentLayoutF>
              <ContentLayoutF.SlotContainer css={{ justifySelf: 'center', width: '100%', ...BS_REVERSE }}>
                {purposeId ? (
                  <ForecasterPurposeProvider>
                    <ContentLayoutF.Slot1>
                      <PurposeSettingsCard />
                    </ContentLayoutF.Slot1>
                    <ContentLayoutF.Slot2 css={PROJECTION_SLOT_CSS}>
                      <RetirementSpendingPowerCard />
                      <PurposeProjectionCard />
                      <HowToSaveCard />
                      <PremiumOnboardingCard />
                    </ContentLayoutF.Slot2>
                  </ForecasterPurposeProvider>
                ) : (
                  <ForecasterAccountProvider accountId={accountId}>
                    <ContentLayoutF.Slot1>
                      <AccountSettingsCard />
                    </ContentLayoutF.Slot1>
                    <ContentLayoutF.Slot2 css={PROJECTION_SLOT_CSS}>
                      <AccountProjectionCard />
                      <PremiumOnboardingCard />
                    </ContentLayoutF.Slot2>
                  </ForecasterAccountProvider>
                )}
              </ContentLayoutF.SlotContainer>
            </ContentLayoutF>
          </PancakeStack.Main>
        </PancakeStack>
      </WithLayout>
    </ErrorBoundary>
  );
};
